import type {
  DirectoryMember,
  MemberDepartment,
  MembershipStatus,
  SuspensionReason,
} from "@closer/shared";
import {
  MEMBERSHIP_COMMAND_LABELS,
  MEMBERSHIP_CONSEQUENCES,
  nextMembershipStatuses,
} from "@closer/shared";
import { Badge, Button, Checkbox, FormField, Modal, Select, Textarea, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * Üyelik komutları (CLOS-189 `[051][S03]`).
 *
 * ### Sayfa domain state'ini YAZMIYOR
 *
 * Kartın sınırı açık: *"sayfa domain state'ini doğrudan yazmaz"* ve *"kaynak command'ların
 * permission, kapasite, son yönetici ve yaşam döngüsü kurallarını bypass etmez."* Bu
 * bileşen yalnız `[038]`, `[039]` uçlarını çağırıyor; hiçbir kuralı yeniden yazmıyor.
 *
 * ### Hangi komutun sunulacağını GEÇİŞ TABLOSU söylüyor
 *
 * ⚠ `nextMembershipStatuses(uye.status)` — sayfada elle yazılmış bir liste YOK. Elle
 * yazılsaydı `[038]`'in tablosu değiştiğinde (7 Ağustos'ta `suspended → ended` açıldığı
 * gibi) arayüz eski kuralı göstermeye devam eder, kullanıcı tıklar ve 409 alırdı.
 *
 * ### Sonuçlar onaydan ÖNCE
 *
 * Liste `@closer/shared`'dan ve sunucu komut yanıtında AYNI listeyi döndürüyor. İki kopya
 * olsaydı onaydan önce gösterilen sonuçlarla sonradan raporlananlar sessizce ayrışırdı.
 */

type Rol = { id: string; name: string; archivedAt?: string | null };

/** Açık iletişim kutusu: bir durum geçişi ya da rol değişikliği. */
export type MemberAction =
  { kind: "status"; to: MembershipStatus } | { kind: "role" } | { kind: "departments" };

const DURUM_YOLLARI: Record<MembershipStatus, string> = {
  active: "reactivate",
  suspended: "suspend",
  ended: "end",
};

const BASARI_METINLERI: Record<MembershipStatus, string> = {
  active: "Üyelik yeniden aktifleştirildi",
  suspended: "Üyelik askıya alındı",
  ended: "Üyelik sona erdirildi",
};

export interface MemberActionsProps {
  tenantId: string;
  member: DirectoryMember;
  suspensionReasons: readonly SuspensionReason[];
  /**
   * ATANABİLİR departmanlar — sunucudan ve yalnız AKTİF olanlar (CLOS-189 `[051][S05]`).
   * Arşivliye atama sunucuda 409; arayüz onu hiç sunmuyor.
   */
  departments: readonly MemberDepartment[];
  /** Menü açıkken satırın hangi üyeye ait olduğu erişilebilir adda taşınıyor. */
  onDone?: () => void;
}

export function MemberActions({
  tenantId,
  member,
  suspensionReasons,
  departments,
  onDone,
}: MemberActionsProps) {
  const [acik, setAcik] = useState(false);
  const [eylem, setEylem] = useState<MemberAction | null>(null);

  const gecisler = nextMembershipStatuses(member.status);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={acik}
        onClick={() => setAcik((o) => !o)}
        aria-label={`${member.name} işlemleri`}
      >
        İşlemler
      </Button>

      {acik ? (
        <div className="flex flex-wrap gap-1">
          {gecisler.map((hedef) => (
            <Button
              key={hedef}
              variant="ghost"
              size="sm"
              onClick={() => setEylem({ kind: "status", to: hedef })}
            >
              {MEMBERSHIP_COMMAND_LABELS[hedef]}
            </Button>
          ))}
          <Button variant="ghost" size="sm" onClick={() => setEylem({ kind: "role" })}>
            Rolü değiştir
          </Button>
          {/*
            ⚠ Departman ATAMASI burada, Departmanlar sayfasında DEĞİL (CLOS-190: "atama
            veya çıkarma command'ı üretmez"). İki sayfaya birden koymak, aynı komutun iki
            farklı çağrılış biçimini ve gün gelip ayrışmasını getirirdi.
          */}
          <Button variant="ghost" size="sm" onClick={() => setEylem({ kind: "departments" })}>
            Departmanlar
          </Button>
        </div>
      ) : null}

      {eylem ? (
        <MemberActionDialog
          tenantId={tenantId}
          member={member}
          action={eylem}
          suspensionReasons={suspensionReasons}
          departments={departments}
          onClose={() => setEylem(null)}
          onDone={() => {
            setEylem(null);
            setAcik(false);
            onDone?.();
          }}
        />
      ) : null}
    </>
  );
}

function MemberActionDialog({
  tenantId,
  member,
  action,
  suspensionReasons,
  departments,
  onClose,
  onDone,
}: {
  tenantId: string;
  member: DirectoryMember;
  action: MemberAction;
  suspensionReasons: readonly SuspensionReason[];
  departments: readonly MemberDepartment[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [gerekce, setGerekce] = useState(suspensionReasons[0]?.code ?? "");
  const [not, setNot] = useState("");
  const [roleId, setRoleId] = useState(member.roleId ?? "");
  const [departmentId, setDepartmentId] = useState(departments[0]?.id ?? "");
  const [ana, setAna] = useState(false);

  /**
   * Rol listesi SUNUCUDAN. Sayfa kendi rol kümesini sabitleseydi özel roller (`[028]`)
   * hiç görünmez ve arşivlenmiş bir rol seçilebilir kalırdı.
   */
  const rolSorgusu = useQuery({
    queryKey: tenantKey(tenantId, "roles"),
    enabled: action.kind === "role",
    queryFn: async () => (await api<Rol[]>("/api/roles", { tenantId })).data,
  });

  /**
   * Departman komutu AYRI bir mutation: durum/rol komutu "Onayla" ile tek seferde biterken
   * departman ekranı açık kalıp birden fazla bağ kurulmasına izin veriyor. Tek mutation'a
   * sıkıştırmak, iki farklı etkileşim biçimini tek bayrağın arkasına saklamak olurdu.
   */
  const departmanKomutu = useMutation({
    mutationFn: async (istek: {
      kind: "assign" | "unassign";
      departmentId: string;
      isPrimary?: boolean;
    }) => {
      const taban = `/api/tenants/${tenantId}/members/${member.id}/departments`;
      if (istek.kind === "assign") {
        await api(taban, {
          method: "POST",
          body: JSON.stringify({ departmentId: istek.departmentId, isPrimary: istek.isPrimary }),
          tenantId,
        });
        return istek.kind;
      }
      await api(`${taban}/${istek.departmentId}`, { method: "DELETE", tenantId });
      return istek.kind;
    },
    onSuccess: async (kind) => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "members") });
      toast.success(kind === "assign" ? "Departman atandı" : "Departman bağlantısı kaldırıldı");
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı, tekrar deneyin.",
      ),
  });

  const komut = useMutation({
    mutationFn: async () => {
      if (action.kind === "role") {
        await api(`/api/tenants/${tenantId}/members/${member.id}/role`, {
          method: "PATCH",
          body: JSON.stringify({ roleId }),
          tenantId,
        });
        return;
      }
      if (action.kind === "departments") return;
      await api(`/api/tenants/${tenantId}/members/${member.id}/${DURUM_YOLLARI[action.to]}`, {
        method: "POST",
        body: JSON.stringify(not ? { reason: gerekce, note: not } : { reason: gerekce }),
        tenantId,
      });
    },
    onSuccess: async () => {
      /**
       * Kapasite de tazeleniyor: sona erdirme koltuk bırakıyor ve özet eski sayıyı
       * göstermeye devam etseydi yönetici "neden hâlâ davet edemiyorum?" derdi.
       */
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "members") }),
        queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "capacity") }),
      ]);
      if (action.kind !== "departments") {
        toast.success(action.kind === "role" ? "Rol güncellendi" : BASARI_METINLERI[action.to]);
      }
      onDone();
    },
    onError: (error) => {
      /**
       * ⚠ SUNUCUNUN cümlesi gösteriliyor. Son yönetici koruması (CLOS-90) ya da kapasite
       * reddinde "bir şeyler ters gitti" deseydik, yönetici neden reddedildiğini — ve tek
       * yöneticiyi koruduğumuzu — hiç öğrenemezdi.
       */
      toast.error(
        error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı, tekrar deneyin.",
      );
    },
  });

  const baslik =
    action.kind === "role"
      ? `${member.name} rolünü değiştir`
      : action.kind === "departments"
        ? `${member.name} departmanları`
        : `${member.name}: ${MEMBERSHIP_COMMAND_LABELS[action.to]}`;

  return (
    <Modal
      open
      onClose={onClose}
      title={baslik}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {action.kind === "departments" ? "Kapat" : "Vazgeç"}
          </Button>
          {/*
            Departman ekranında tek bir "Onayla" YOK: her atama/kaldırma kendi başına bir
            komut ve pencere açık kalıyor. Tek bir onaya bağlasaydık yönetici birden fazla
            departman eklemek için pencereyi her seferinde yeniden açardı.
          */}
          {action.kind === "departments" ? null : (
            <Button loading={komut.isPending} onClick={() => komut.mutate()}>
              Onayla
            </Button>
          )}
        </div>
      }
    >
      {action.kind === "departments" ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <FormField label="Departman">
                <Select
                  value={departmentId}
                  onChange={(event) => setDepartmentId(event.target.value)}
                >
                  {departments.map((departman) => (
                    <option key={departman.id} value={departman.id}>
                      {departman.name}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
            <Button
              loading={departmanKomutu.isPending}
              disabled={!departmentId}
              onClick={() =>
                departmanKomutu.mutate({ kind: "assign", departmentId, isPrimary: ana })
              }
            >
              Ata
            </Button>
          </div>

          {/*
            ⚠ ANA DEPARTMAN bir SEÇENEK, zorunlu bir adım değil (Departmanlar Kanonu §5.2:
            "department kullanımı opsiyonel ise primary zorunlu değildir"). Zorunlu kılsaydık
            departmanı yalnız etiket olarak kullanan bir organizasyon her atamada anlamsız
            bir karar vermek zorunda kalırdı.

            ⚠ Metin "ana ekibi" diyor, "primary" demiyor: kullanıcı teknik terimi görmez.
          */}
          <div className="flex flex-col gap-1">
            <Checkbox
              checked={ana}
              onChange={(event) => setAna(event.target.checked)}
              label="Bu kişinin ana ekibi olsun"
            />
            <p className="text-xs text-fg-muted">
              Ana ekip yalnız kimin nerede çalıştığını söyler; hiçbir yetki vermez.
            </p>
          </div>

          {member.departments.length === 0 ? (
            <p className="text-sm text-fg-muted">Bu kullanıcı hiçbir departmanda değil.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {member.departments.map((departman) => (
                <li key={departman.id} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm text-fg">
                    {departman.name}
                    {/* ⚠ Rozet yalnız ana ekipte: her satıra etiket koymak ayrımı görünmez kılardı. */}
                    {departman.isPrimary ? <Badge tone="info">Ana ekip</Badge> : null}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`${departman.name} bağlantısını kaldır`}
                    onClick={() =>
                      departmanKomutu.mutate({
                        kind: "unassign",
                        departmentId: departman.id,
                      })
                    }
                  >
                    Kaldır
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : action.kind === "role" ? (
        <FormField label="Rol">
          <Select value={roleId} onChange={(event) => setRoleId(event.target.value)}>
            {(rolSorgusu.data ?? [])
              .filter((rol) => !rol.archivedAt)
              .map((rol) => (
                <option key={rol.id} value={rol.id}>
                  {rol.name}
                </option>
              ))}
          </Select>
        </FormField>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Sonuçlar onaydan ÖNCE: geri alınabilir mi, koltuk boşalıyor mu, kişi ne kaybediyor. */}
          <ul className="list-disc pl-5 text-sm text-fg-muted">
            {MEMBERSHIP_CONSEQUENCES[action.to].map((satir) => (
              <li key={satir}>{satir}</li>
            ))}
          </ul>
          <FormField label="Gerekçe" required>
            <Select value={gerekce} onChange={(event) => setGerekce(event.target.value)}>
              {suspensionReasons.map((sebep) => (
                <option key={sebep.code} value={sebep.code}>
                  {sebep.label}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Not" hint="İsteğe bağlı; denetim kaydına yazılır">
            <Textarea rows={2} value={not} onChange={(event) => setNot(event.target.value)} />
          </FormField>
        </div>
      )}
    </Modal>
  );
}
